<script>
/**
 * @component PatientMonitoring
 * @description Vista standalone de seguimiento del paciente.
 *              Carga datos de paciente, alertas y vitales, y renderiza
 *              SeguimientoTab con los modales necesarios.
 * @example <PatientMonitoring />
 */
import SeguimientoTab from "@/components/patient/dashboard/SeguimientoTab";
import Spinner from "@/components/ui/Spinner";
import ModalTaskGraph from "@/components/patient/dashboard/ModalTaskGraph";
import ModalPatientTasksList from "@/components/patient/dashboard/ModalPatientTasksList";
import ModalManualAddTask from "@/components/patient/dashboard/ModalManualAddTask";
import ModalTaskDetail from "@/components/patient/dashboard/ModalTaskDetail";
import PatientLabels from "@/components/patient/dashboard/PatientLabels";
import patientService from "@/services/patient";
import alertsService from "@/services/alerts";
import helperService from "@/services/helper";
import api from "@/services/api";
import { mapGetters } from "vuex";

/**
 * @constant {string[]} LAB_NAMES
 * @description Nombres de vitales que son resultados de laboratorio. Se usan
 *   para separar los vitales clínicos (presión, peso, talla, temperatura),
 *   que van en las cards de "Signos vitales", de los de laboratorio, que van
 *   en la tabla de laboratorio.
 */
const LAB_NAMES = [
  "Hemoglobina (H)",
  "Hemoglobina (M)",
  "Hematocrito (H)",
  "Hematocrito (M)",
  "Glóbulos Blancos",
  "Plaquetas",
  "Glucemia",
  "HbA1c",
  "Urea",
  "Creatinina",
  "Ácido Úrico",
  "Insulina",
  "Colesterol Total",
  "Colesterol HDL (H)",
  "Colesterol HDL (M)",
  "Colesterol LDL",
  "Triglicéridos",
  "GPT (ALAT)",
  "GOT (ASAT)",
  "Bilirrubina Total",
  "TSH",
  "T4 Libre",
  "PSA",
];

export default {
  name: "PatientMonitoring",

  components: {
    Spinner,
    SeguimientoTab,
    ModalTaskGraph,
    ModalPatientTasksList,
    ModalManualAddTask,
    ModalTaskDetail,
    PatientLabels,
  },

  data() {
    return {
      patientId: this.$route.params.id || this.$store.state.user._id,
      patient: {},
      isLoading: false,
      alerts: [],
      vitals: [],
      activityAlerts: [],
      alertsLimit: 6,
      /** @description Lab vitals obtenidos del endpoint histórico (sin filtro de ventana temporal) */
      historicalLabVitals: {},
      modal: "",
      selectedType: "",
      selectedTask: null,
      showDialogModal: false,
    };
  },

  created() {
    this.loadInitialData();
  },

  computed: {
    ...mapGetters(["isAdmin", "isDoctor", "isPatient"]),

    /**
     * @description Vitales de laboratorio. Cuando el rol es paciente usa historicalLabVitals
     * (endpoint sin ventana temporal) para mostrar resultados de lab de cualquier fecha.
     * En vista médico filtra de this.vitals normalmente.
     * @returns {Object}
     */
    labVitals() {
      if (this.isPatient) return this.historicalLabVitals;
      return Object.fromEntries(
        Object.entries(this.vitals).filter(([name]) => LAB_NAMES.includes(name))
      );
    },

    /**
     * @description Vitales clínicos (no-lab) para las cards de "Signos
     *   vitales". Para pacientes usa `historicalLabVitals` (endpoint
     *   `/tasks/vitals/type/patient/history`, SIN filtro `scheduledAt`),
     *   porque las tomas ad-hoc de presión/peso/talla/temperatura no tienen
     *   `scheduledAt` y el fetch principal (`scheduledAt <= now`) las excluía
     *   → las cards mostraban "—" aunque las cargas existían (UC-REG-294).
     *   Para médico mantiene el filtrado de `this.vitals`.
     * @returns {Object}
     */
    clinicalVitals() {
      const source = this.isPatient ? this.historicalLabVitals : this.vitals;
      return Object.fromEntries(
        Object.entries(source || {}).filter(
          ([name]) => !LAB_NAMES.includes(name)
        )
      );
    },
  },

  methods: {
    /**
     * @description Carga datos iniciales del paciente y sus alertas.
     *              Para pacientes logueados usa getMe (sin toast de error).
     */
    async loadInitialData() {
      this.isLoading = true;

      try {
        const query = {
          scheduledAt: { $lte: new Date() },
        };

        const patientPromise = this.isPatient
          ? patientService.getMe(this.patientId)
          : patientService.getById(this.patientId);

        const alertsPromise = this.isPatient
          ? alertsService
              .getAllAlerts({ patientID: this.patientId, query, silent: true })
              .catch(() => [])
          : alertsService.getAllAlerts({ patientID: this.patientId, query });

        // Cuando el rol es paciente, obtener lab vitals del endpoint histórico
        // (sin filtro de ventana temporal) para mostrar resultados de cualquier fecha.
        const historicalLabPromise = this.isPatient
          ? api
              .get("/tasks/vitals/type/patient/history", {
                params: { sort: { completedAt: -1 } },
                getErrorMessage: () => null,
              })
              .then((res) => res.data?.docs || [])
              .catch(() => [])
          : Promise.resolve([]);

        const [patient, alerts, historicalDocs] = await Promise.all([
          patientPromise,
          alertsPromise,
          historicalLabPromise,
        ]);

        this.patient = patient;
        this.$bus.$emit("update-title", helperService.getFullName(patient));
        this.activityAlerts = alertsService.getActivityAlerts({ alerts });
        const { vitals, rest } = alertsService.separateAlerts({ alerts });
        this.vitals = alertsService.groupBy({
          alerts: vitals,
          limit: this.alertsLimit,
        });
        this.alerts = alertsService.groupBy({
          alerts: rest,
          limit: this.alertsLimit,
          prop: "type",
        });

        // Agrupar lab vitals históricos por nombre (sin límite temporal)
        if (historicalDocs.length > 0) {
          this.historicalLabVitals = alertsService.groupBy({
            alerts: historicalDocs.map((d) => ({ ...d, type: "vitals" })),
            limit: this.alertsLimit,
          });
        }
      } catch (error) {
        if (this.isPatient) {
          console.warn("Error al cargar datos del paciente:", error);
        } else {
          console.error("Error al cargar datos del paciente:", error);
        }
      } finally {
        this.isLoading = false;
      }
    },

    /**
     * @description Muestra un modal disparado por SeguimientoTab
     * @param {Object} payload - { modal, task, type, appointments }
     */
    showModal({ modal, task, type, appointments }) {
      this.modal = modal;
      this.selectedTask = task;
      this.selectedType = type;
      this.appointments = appointments;
      this.showDialogModal = true;
    },

    /** @description Cierra el modal activo */
    closeModal() {
      this.modal = "";
      this.selectedType = null;
      this.selectedTask = null;
      this.showDialogModal = false;
    },

    /**
     * @description Callback al guardar tarea manual de vital
     * @param {Object} task
     */
    onSaveManualTask(task) {
      this.closeModal();
      this.vitals[task.name].unshift(task);
      if (this.vitals[task.name].length > this.alertsLimit) {
        this.vitals[task.name].pop();
      }
    },
  },

  watch: {
    "$route.params.id": {
      handler: function () {
        this.patientId = this.$route.params.id || this.$store.state.user._id;
        this.loadInitialData();
      },
    },
  },
};
</script>

<template lang="pug">
section.patient-monitoring
  spinner(v-if="isLoading")
  .patient-monitoring__content(v-else)
    patient-labels(
      v-show="!isPatient && !isDoctor"
      v-model="patient.labels"
      :patientId="patient._id"
    )
    seguimiento-tab(
      :patient="patient"
      :vitals="clinicalVitals"
      :labVitals="labVitals"
      :alerts="alerts"
      :activityAlerts="activityAlerts"
      @show-modal="showModal"
    )

  //- Modales disparados por SeguimientoTab
  modal-task-graph(
    v-if="modal === 'task-graph'"
    :patientId="patientId"
    :name="selectedTask"
    :showDialogModal="showDialogModal"
    @close="closeModal"
  )
  modal-patient-tasks-list(
    v-if="modal === 'tasks-table'"
    :patientId="patientId"
    :name="selectedTask"
    :type="selectedType"
    :showDialogModal="showDialogModal"
    @close="closeModal"
  )
  modal-manual-add-task(
    v-if="modal === 'add-manual-task'"
    :newTask="selectedTask"
    :patientId="patientId"
    :showDialogModal="showDialogModal"
    @close="closeModal"
    @onSave="onSaveManualTask"
  )
  modal-task-detail(
    v-if="modal === 'task-detail'"
    :task="selectedTask"
    :showDialogModal="showDialogModal"
    @close="closeModal"
  )
</template>

<style lang="scss" scoped>
.patient-monitoring {
  padding: 0;
}

.patient-monitoring__content {
  background: #fff;
  border-radius: 10px;
  box-shadow: 0 0 5px 0 rgba(0, 0, 0, 0.1);
  padding: 20px;
}
</style>
